<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick } from 'vue'
import BarChart from './BarChart.vue'
import DateRangePicker from './DateRangePicker.vue'
import { Settings, Calendar, X, Filter } from 'lucide-vue-next'

import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import { Pie, Line, Bar } from 'vue-chartjs'
import { Download } from 'lucide-vue-next'
import jsPDF from 'jspdf'
import ChartDataLabels from 'chartjs-plugin-datalabels'

import 'jspdf-autotable'
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
)

const store = useStore()
const route = useRoute()

const loading = ref(true)
const npsData = computed(() => store.getters['analytics/npsData'])

const start = new Date()
start.setDate(1)
start.setHours(0, 0, 0, 0)
const end = new Date()
const showFiltersMenu = ref(false)
const showMobileMenu = ref(false)

// Update breakpoint logic
const screenWidth = ref(window.innerWidth)
const screenHeight = ref(window.innerHeight)

// Adjusted breakpoint logic for iPad Air and similar tablets
const isMobile = computed(() => screenWidth.value < 640)
const isTabletLandscape = computed(
  () => screenWidth.value >= 768 && screenWidth.value <= 1024
)
const showTwoColumns = computed(() => {
  // Mobile: always single column
  if (screenWidth.value < 640) return false

  // Tablet landscape: single column for better readability
  if (screenWidth.value >= 768 && screenWidth.value <= 1024) return false

  // Large screens: two columns
  return screenWidth.value > 1024
})

const dateRange = ref({
  start: start,
  end: end
})

const handleResize = () => {
  screenWidth.value = window.innerWidth
  screenHeight.value = window.innerHeight
  if (isMobile.value && showMobileMenu.value) {
    showMobileMenu.value = false
  }
}

// Add window resize event listener
onMounted(() => {
  window.addEventListener('resize', handleResize)
  getNPSData(displayStartDate, displayEndDate)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

const handleDateRangeChange = () => {
  const startDate = new Date(dateRange.value.start)
  const endDate = new Date(dateRange.value.end)

  const from = formatLocalDate(startDate)
  const to = formatLocalDate(endDate)

  displayStartDate = from
  displayEndDate = to
  getNPSData(from, to)

  if (isMobile.value) {
    showMobileMenu.value = false
  }
}

const filterType = ref('app,survey_sparrow')
const filterOptions = [
  { value: 'app,survey_sparrow', label: 'All Channels' },
  { value: 'app', label: 'App' },
  { value: 'survey_sparrow', label: 'Email' }
]

const handleFilterChange = () => {
  getNPSData(
    formatLocalDate(dateRange.value.start),
    formatLocalDate(dateRange.value.end)
  )
}

const formatLocalDate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const startDate = computed({
  get: () => formatLocalDate(dateRange.value.start),
  set: (value) => {
    if (!value) return
    const start = new Date(value)
    start.setHours(0, 0, 0, 0)
    dateRange.value.start = start
  }
})

const endDate = computed({
  get: () => formatLocalDate(dateRange.value.end),
  set: (value) => {
    if (!value) return
    const end = new Date(value)
    end.setHours(23, 59, 59, 999)
    dateRange.value.end = end
  }
})

let displayStartDate =
  route.query.date_from ?? formatLocalDate(dateRange.value.start)

let displayEndDate = route.query.date_to ?? formatLocalDate(dateRange.value.end)

const getNPSData = async (from, to) => {
  try {
    loading.value = true
    const params = {
      start_date: from,
      end_date: to,
      source: filterType.value
    }
    await store.dispatch('analytics/getNPSData', params)
  } catch (error) {
    console.error('Error:', error)
  } finally {
    loading.value = false
  }
}

const setPredefinedRange = (range) => {
  const now = new Date()
  const start = new Date()

  switch (range) {
    case 'today':
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
    case 'yesterday':
      start.setDate(start.getDate() - 1)
      start.setHours(0, 0, 0, 0)
      const yesterdayEnd = new Date(start)
      yesterdayEnd.setHours(23, 59, 59, 999)
      dateRange.value = { start, end: yesterdayEnd }
      break
    case 'week':
      start.setDate(start.getDate() - 7)
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
    case 'month':
      start.setMonth(start.getMonth() - 1)
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
    case 'quarter':
      start.setMonth(start.getMonth() - 3)
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
  }

  handleDateRangeChange()
}

const pieChartData = computed(() => {
  if (!npsData.value?.overall) return null
  const total = npsData.value.overall.total_responses
  return {
    labels: [
      `Detractors (${npsData.value.overall.detractors})`,
      `Passives (${npsData.value.overall.passives})`,
      `Promoters (${npsData.value.overall.promoters})`
    ],
    datasets: [
      {
        data: [
          npsData.value.overall.detractors,
          npsData.value.overall.passives,
          npsData.value.overall.promoters
        ],
        backgroundColor: ['#ef4444', '#f59e0b', '#22c55e']
      }
    ]
  }
})

const languageChartData = computed(() => {
  if (!npsData.value?.by_language) return null
  return {
    labels: Object.keys(npsData.value.by_language).map((code) => {
      try {
        const languageNames = new Intl.DisplayNames(['en'], {
          type: 'language'
        })
        return languageNames.of(code)
      } catch (error) {
        return code
      }
    }),
    datasets: [
      {
        label: 'NPS Score',
        data: Object.values(npsData.value.by_language).map(
          (data) => data.nps_score
        ),
        backgroundColor: '#3b82f6',
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        }
      },
      {
        label: 'Total Responses',
        data: Object.values(npsData.value.by_language).map(
          (data) => data.total_responses
        ),
        backgroundColor: '#64748b',
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        }
      }
    ]
  }
})

const industryChartData = computed(() => {
  if (!npsData.value?.by_industry) return null
  return {
    labels: Object.keys(npsData.value.by_industry).map((code) => {
      try {
        return code
          .split(/[_-]/)
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ')
      } catch (error) {
        return code
      }
    }),
    datasets: [
      {
        label: 'NPS Score',
        data: Object.values(npsData.value.by_industry).map(
          (data) => data.nps_score
        ),
        backgroundColor: '#3b82f6',
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        }
      },
      {
        label: 'Total Responses',
        data: Object.values(npsData.value.by_industry).map(
          (data) => data.total_responses
        ),
        backgroundColor: '#64748b',
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        }
      }
    ]
  }
})

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: '#fff',
        font: { size: isTabletLandscape.value ? 10 : 12 }
      }
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: isTabletLandscape.value ? 8 : 12
    },
    datalabels: {
      display: true,
      color: '#fff',
      font: {
        weight: 'bold',
        size: isTabletLandscape.value ? 11 : 13
      },
      formatter: (value, context) => {
        // For pie chart
        if (context.chart.config.type === 'pie') {
          const dataset = context.chart.data.datasets[0]
          const total = dataset.data.reduce((acc, val) => acc + val, 0)
          const percentage = ((value / total) * 100).toFixed(1)
          return `${value} (${percentage}%)`
        }
        // For bar charts
        return `${value}%`
      }
    }
  },
  scales: {
    y: {
      ticks: {
        color: '#fff',
        font: { size: isTabletLandscape.value ? 9 : 11 }
      },
      grid: {
        color: '#374151'
      }
    },
    x: {
      ticks: {
        color: '#fff',
        font: { size: isTabletLandscape.value ? 9 : 11 }
      },
      grid: {
        color: '#374151'
      }
    }
  }
}))

const pieChartOptions = computed(() => ({
  ...chartOptions.value,
  plugins: {
    ...chartOptions.value.plugins,
    datalabels: {
      ...chartOptions.value.plugins.datalabels,
      anchor: 'center',
      align: 'center',
      offset: 0,
      font: {
        weight: 'bold',
        size: isTabletLandscape.value ? 10 : 12
      }
    }
  }
}))

const lineChartOptions = computed(() => ({
  ...chartOptions.value,
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    ...chartOptions.value.plugins,
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: isTabletLandscape.value ? 8 : 12,
      mode: 'index',
      intersect: false,
      callbacks: {
        title: (tooltipItems) => tooltipItems[0].label,
        label: (tooltipItem) => {
          const label = tooltipItem.dataset.label
          const value = tooltipItem.raw
          return `${label}: ${value}`
        },
        labelColor: (tooltipItem) => {
          const colors = {
            'NPS Score': { backgroundColor: '#3b82f6', borderColor: '#3b82f6' },
            'Monthly NPS Score': {
              backgroundColor: '#3b82f6',
              borderColor: '#3b82f6'
            },
            'Total Responses': {
              backgroundColor: '#f59e0b',
              borderColor: '#f59e0b'
            },
            'Monthly Responses': {
              backgroundColor: '#f59e0b',
              borderColor: '#f59e0b'
            },
            'Overall NPS Score': {
              backgroundColor: '#3b82f6',
              borderColor: '#3b82f6'
            },
            'Cumulative Responses': {
              backgroundColor: '#f59e0b',
              borderColor: '#f59e0b'
            },
            'Actual NPS': {
              backgroundColor: '#6366f1',
              borderColor: '#6366f1'
            },
            '3M Weighted Average': {
              backgroundColor: '#3b82f6',
              borderColor: '#3b82f6'
            },
            '3M Rolling Average': {
              backgroundColor: '#3b82f6',
              borderColor: '#3b82f6'
            }
          }
          return (
            colors[tooltipItem.dataset.label] || {
              backgroundColor: tooltipItem.dataset.borderColor,
              borderColor: tooltipItem.dataset.borderColor
            }
          )
        },
        labelTextColor: () => '#ffffff'
      }
    },
    datalabels: {
      display: false
    }
  },
  scales: {
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      ticks: {
        color: '#fff',
        font: { size: isTabletLandscape.value ? 9 : 11 }
      },
      grid: {
        color: '#374151'
      },
      title: {
        display: true,
        text: 'NPS Score',
        color: '#fff',
        font: { size: isTabletLandscape.value ? 10 : 12 }
      },
      min: -100,
      max: 100
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      ticks: {
        color: '#fff',
        font: { size: isTabletLandscape.value ? 9 : 11 }
      },
      grid: {
        drawOnChartArea: false
      },
      title: {
        display: true,
        text: 'Total Responses',
        color: '#fff',
        font: { size: isTabletLandscape.value ? 10 : 12 }
      },
      min: 0
    }
  }
}))

const cumulativeChartOptions = computed(() => ({
  ...lineChartOptions.value,
  scales: {
    ...lineChartOptions.value.scales,
    y: {
      ...lineChartOptions.value.scales.y,
      title: {
        display: true,
        text: 'Cumulative NPS',
        color: '#fff',
        font: { size: isTabletLandscape.value ? 10 : 12 }
      }
    }
  }
}))

// monthly

const hasValidMonthlyMetrics = computed(() => {
  return (
    npsData.value &&
    npsData.value.monthly_metrics &&
    Object.keys(npsData.value.monthly_metrics).length > 0
  )
})

const formatMonthDisplay = (yearMonth) => {
  const [year, month] = yearMonth.split('-').map((num) => parseInt(num, 10))
  const date = new Date(year, month - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

const calculateWeightedAverage = (data) => {
  return data.map((_, index, array) => {
    if (index < 2) return null
    const window = array.slice(index - 2, index + 1)
    const weighted =
      window[2].nps_score * 0.5 +
      window[1].nps_score * 0.3 +
      window[0].nps_score * 0.2
    return parseFloat(weighted.toFixed(1))
  })
}

const calculateRollingAverage = (data, windowSize) => {
  return data.map((_, index, array) => {
    if (index < windowSize - 1) return null
    const window = array.slice(Math.max(0, index - windowSize + 1), index + 1)
    const sum = window.reduce((acc, val) => acc + val.nps_score, 0)
    return parseFloat((sum / window.length).toFixed(1))
  })
}

const npsViewType = ref('normal')

const calculateWilsonInterval = (
  promoters,
  detractors,
  total,
  confidence = 0.95
) => {
  if (total === 0) return { lower: 0, upper: 0, score: 0 }
  const z = 1.96
  const pHat = promoters / total
  const denominator = 1 + (z * z) / total
  const adjustment =
    (z / denominator) *
    Math.sqrt((pHat * (1 - pHat) + (z * z) / (4 * total)) / total)
  const pCenter = (pHat + (z * z) / (2 * total)) / denominator
  const pLower = Math.max(0, pCenter - adjustment)
  const pUpper = Math.min(1, pCenter + adjustment)

  const dHat = detractors / total
  const dDenominator = 1 + (z * z) / total
  const dAdjustment =
    (z / dDenominator) *
    Math.sqrt((dHat * (1 - dHat) + (z * z) / (4 * total)) / total)
  const dCenter = (dHat + (z * z) / (2 * total)) / dDenominator
  const dLower = Math.max(0, dCenter - dAdjustment)
  const dUpper = Math.min(1, dCenter + dAdjustment)

  const score = ((promoters - detractors) / total) * 100
  const lower = ((pLower - dUpper) * 100).toFixed(1)
  const upper = ((pUpper - dLower) * 100).toFixed(1)

  return { lower: parseFloat(lower), upper: parseFloat(upper), score }
}

const monthlyChartData = computed(() => {
  if (!hasValidMonthlyMetrics.value) return null

  try {
    const sortedYearMonths = Object.keys(npsData.value.monthly_metrics).sort()
    const monthlyData = sortedYearMonths.map((yearMonth) => ({
      date: formatMonthDisplay(yearMonth),
      nps_score: npsData.value.monthly_metrics[yearMonth].nps_score,
      responses: npsData.value.monthly_metrics[yearMonth].responses,
      promoters: npsData.value.monthly_metrics[yearMonth].promoters,
      detractors: npsData.value.monthly_metrics[yearMonth].detractors
    }))

    const datasets = [
      {
        label: 'Monthly Responses',
        data: monthlyData.map((m) => m.responses),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y1'
      }
    ]

    switch (npsViewType.value) {
      case 'normal': {
        const confidenceData = monthlyData.map((m) =>
          calculateWilsonInterval(m.promoters, m.detractors, m.responses)
        )

        datasets.unshift({
          label: '95% Confidence Interval',
          data: monthlyData.map((_, i) => ({
            x: monthlyData[i].date,
            y: confidenceData[i].lower,
            y1: confidenceData[i].upper
          })),
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: 'transparent',
          fill: true,
          tension: 0.4,
          yAxisID: 'y',
          type: 'line',
          tooltip: {
            callbacks: {
              label: (context) => {
                const dataPoint = context.raw
                return `CI: [${dataPoint.y.toFixed(1)}, ${dataPoint.y1.toFixed(
                  1
                )}]`
              }
            }
          }
        })

        datasets.unshift({
          label: 'Monthly NPS Score',
          data: monthlyData.map((m) => m.nps_score),
          borderColor: '#3b82f6',
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: '#3b82f6',
          tension: 0.4,
          fill: false,
          yAxisID: 'y'
        })
        break
      }

      case 'weighted':
        datasets.unshift({
          label: '3M Weighted Average',
          data: calculateWeightedAverage(monthlyData),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: '#3b82f6',
          tension: 0.4,
          fill: true,
          yAxisID: 'y'
        })
        datasets.unshift({
          label: 'Actual NPS',
          data: monthlyData.map((m) => m.nps_score),
          borderColor: '#6366f1',
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#6366f1',
          borderDash: [5, 5],
          tension: 0.4,
          fill: false,
          yAxisID: 'y'
        })
        break

      case 'rolling':
        datasets.unshift({
          label: '3M Rolling Average',
          data: calculateRollingAverage(monthlyData, 3),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: '#3b82f6',
          tension: 0.4,
          fill: true,
          yAxisID: 'y'
        })
        datasets.unshift({
          label: 'Actual NPS',
          data: monthlyData.map((m) => m.nps_score),
          borderColor: '#6366f1',
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#6366f1',
          borderDash: [5, 5],
          tension: 0.4,
          fill: false,
          yAxisID: 'y'
        })
        break
    }

    return {
      labels: monthlyData.map((m) => m.date),
      datasets
    }
  } catch (error) {
    console.error('Error generating monthly chart data:', error)
    return null
  }
})

const cumulativeMonthlyChartData = computed(() => {
  if (!hasValidMonthlyMetrics.value) {
    return null
  }

  try {
    const sortedYearMonths = Object.keys(npsData.value.monthly_metrics).sort()

    let cumulativeResponses = 0
    let cumulativePromoters = 0
    let cumulativeDetractors = 0

    const cumulativeData = sortedYearMonths.map((yearMonth) => {
      const metrics = npsData.value.monthly_metrics[yearMonth]

      cumulativeResponses += metrics.responses
      cumulativePromoters += metrics.promoters
      cumulativeDetractors += metrics.detractors

      const npsScore =
        cumulativeResponses === 0
          ? 0
          : Math.round(
              ((cumulativePromoters - cumulativeDetractors) /
                cumulativeResponses) *
                100
            )

      return {
        date: formatMonthDisplay(yearMonth),
        nps: npsScore,
        responses: cumulativeResponses
      }
    })

    return {
      labels: cumulativeData.map((d) => d.date),
      datasets: [
        {
          label: 'Cumulative NPS',
          data: cumulativeData.map((d) => d.nps),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y'
        },
        {
          label: 'Cumulative Responses',
          data: cumulativeData.map((d) => d.responses),
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y1'
        }
      ]
    }
  } catch (error) {
    console.error('Error generating cumulative monthly chart data:', error)
    return null
  }
})

const dailyChartData = computed(() => {
  if (!npsData.value?.daily_metrics) return null

  const sortedDates = Object.keys(npsData.value.daily_metrics).sort()
  const metrics = sortedDates.map((date) => npsData.value.daily_metrics[date])

  return {
    labels: sortedDates,
    datasets: [
      {
        label: 'NPS Score',
        data: metrics.map((m) => m.nps_score),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y'
      },
      {
        label: 'Total Responses',
        data: metrics.map((m) => m.responses),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y1'
      }
    ]
  }
})

const cumulativeChartData = computed(() => {
  if (!npsData.value?.daily_metrics) return null

  const sortedDates = Object.keys(npsData.value.daily_metrics).sort()
  const metrics = sortedDates.map((date) => npsData.value.daily_metrics[date])

  const cumulativeData = metrics.reduce((acc, metric, index) => {
    const totalPromoters = metrics
      .slice(0, index + 1)
      .reduce((sum, m) => sum + m.promoters, 0)
    const totalDetractors = metrics
      .slice(0, index + 1)
      .reduce((sum, m) => sum + m.detractors, 0)
    const totalResponses = metrics
      .slice(0, index + 1)
      .reduce((sum, m) => sum + m.responses, 0)

    const currentNPS = Math.round(
      ((totalPromoters - totalDetractors) / totalResponses) * 100
    )

    return [
      ...acc,
      {
        responses: totalResponses,
        nps: currentNPS
      }
    ]
  }, [])

  return {
    labels: sortedDates,
    datasets: [
      {
        label: 'Overall NPS Score',
        data: cumulativeData.map((d) => d.nps),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y'
      },
      {
        label: 'Cumulative Responses',
        data: cumulativeData.map((d) => d.responses),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y1'
      }
    ]
  }
})

const pieChartRef = ref(null)
const languageChartRef = ref(null)
const industryChartRef = ref(null)

const themesData = computed(() => {
  if (!npsData.value?.themes || !Array.isArray(npsData.value.themes)) {
    return null
  }
  return npsData.value.themes
})

const selectedImprovementTheme = ref('all')
const selectedSuggestionTheme = ref('all')

const uniqueImprovementThemes = computed(() => {
  if (!themesData.value) return []
  const themes = new Set(
    themesData.value.map((theme) =>
      theme.improvement_theme === 'NULL_IMPROV'
        ? 'None'
        : theme.improvement_theme
    )
  )
  return ['all', ...Array.from(themes)].filter(Boolean)
})

const uniqueSuggestionThemes = computed(() => {
  if (!themesData.value) return []
  const themes = new Set(
    themesData.value.map((theme) => theme.suggestion_theme)
  )
  return ['all', ...Array.from(themes)].filter(Boolean)
})

const filteredThemesData = computed(() => {
  if (!themesData.value) return null

  return themesData.value.filter((theme) => {
    const matchesImprovement =
      selectedImprovementTheme.value === 'all' ||
      (theme.improvement_theme === 'NULL_IMPROV' &&
        selectedImprovementTheme.value === 'None') ||
      theme.improvement_theme === selectedImprovementTheme.value

    const matchesSuggestion =
      selectedSuggestionTheme.value === 'all' ||
      theme.suggestion_theme === selectedSuggestionTheme.value

    return matchesImprovement && matchesSuggestion
  })
})

const generatePDF = () => {
  try {
    const doc = new jsPDF()
    // PDF generation code here - same as original
    const pdfOutput = doc.output('blob')
    const url = window.URL.createObjectURL(pdfOutput)
    const link = document.createElement('a')
    link.href = url
    link.download = 'nps-analysis-report.pdf'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error generating PDF:', error)
  }
}

const monthlyDistributionChartData = computed(() => {
  if (!hasValidMonthlyMetrics.value) {
    return null
  }

  try {
    const sortedYearMonths = Object.keys(npsData.value.monthly_metrics).sort()

    return {
      labels: sortedYearMonths.map((yearMonth) => {
        const metrics = npsData.value.monthly_metrics[yearMonth]
        return `${formatMonthDisplay(yearMonth)} (${metrics.responses})`
      }),
      datasets: [
        {
          label: 'Detractors',
          data: sortedYearMonths.map((yearMonth) => {
            const metrics = npsData.value.monthly_metrics[yearMonth]
            return ((metrics.detractors / metrics.responses) * 100).toFixed(1)
          }),
          rawData: sortedYearMonths.map((yearMonth) => {
            const metrics = npsData.value.monthly_metrics[yearMonth]
            return metrics.detractors
          }),
          backgroundColor: '#ef4444',
          stack: 'stack0'
        },
        {
          label: 'Passives',
          data: sortedYearMonths.map((yearMonth) => {
            const metrics = npsData.value.monthly_metrics[yearMonth]
            return ((metrics.passives / metrics.responses) * 100).toFixed(1)
          }),
          rawData: sortedYearMonths.map((yearMonth) => {
            const metrics = npsData.value.monthly_metrics[yearMonth]
            return metrics.passives
          }),
          backgroundColor: '#f59e0b',
          stack: 'stack0'
        },
        {
          label: 'Promoters',
          data: sortedYearMonths.map((yearMonth) => {
            const metrics = npsData.value.monthly_metrics[yearMonth]
            return ((metrics.promoters / metrics.responses) * 100).toFixed(1)
          }),
          rawData: sortedYearMonths.map((yearMonth) => {
            const metrics = npsData.value.monthly_metrics[yearMonth]
            return metrics.promoters
          }),
          backgroundColor: '#22c55e',
          stack: 'stack0'
        }
      ]
    }
  } catch (error) {
    console.error('Error generating monthly distribution chart data:', error)
    return null
  }
})

const distributionChartOptions = computed(() => ({
  ...chartOptions.value,
  scales: {
    ...chartOptions.value.scales,
    x: {
      ...chartOptions.value.scales.x,
      stacked: true
    },
    y: {
      ...chartOptions.value.scales.y,
      stacked: true,
      title: {
        display: true,
        text: 'Percentage',
        color: '#fff',
        font: { size: isTabletLandscape.value ? 10 : 12 }
      }
    }
  },
  plugins: {
    ...chartOptions.value.plugins,
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: isTabletLandscape.value ? 8 : 12,
      mode: 'index',
      intersect: false,
      callbacks: {
        title: (tooltipItems) => tooltipItems[0].label,
        label: (tooltipItem) => {
          const label = tooltipItem.dataset.label
          const value = tooltipItem.raw
          const count = tooltipItem.dataset.rawData[tooltipItem.dataIndex]
          return `${label}: ${count} (${value}%)`
        },
        labelColor: (tooltipItem) => {
          const colors = {
            Detractors: { backgroundColor: '#ef4444', borderColor: '#ef4444' },
            Passives: { backgroundColor: '#f59e0b', borderColor: '#f59e0b' },
            Promoters: { backgroundColor: '#22c55e', borderColor: '#22c55e' }
          }
          return (
            colors[tooltipItem.dataset.label] || {
              backgroundColor: tooltipItem.dataset.backgroundColor,
              borderColor: tooltipItem.dataset.backgroundColor
            }
          )
        },
        labelTextColor: () => '#ffffff'
      }
    },
    datalabels: {
      ...chartOptions.value.plugins.datalabels,
      color: '#fff',
      font: {
        weight: 'bold',
        size: isTabletLandscape.value ? 10 : 11
      },
      formatter: (value, context) => {
        const count = context.dataset.rawData[context.dataIndex]
        if (count === 0) return ''
        return `${count} (${value}%)`
      }
    }
  }
}))

const overallConfidenceInterval = computed(() => {
  if (!npsData.value?.overall) return null
  return calculateWilsonInterval(
    npsData.value.overall.promoters,
    npsData.value.overall.detractors,
    npsData.value.overall.total_responses
  )
})
</script>

<template>
  <div v-if="loading" class="min-h-screen flex items-center justify-center">
    <div
      class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"
    ></div>
  </div>

  <div
    v-else-if="!npsData"
    class="min-h-screen flex items-center justify-center"
  >
    <div class="text-white">No data available</div>
  </div>

  <div v-else class="min-h-screen">
    <!-- Header Section -->
    <div class="flex flex-col gap-4 mb-8">
      <div
        class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div class="flex-1 min-w-0">
          <h2 class="text-xl font-semibold text-white truncate">
            NPS Dashboard
          </h2>
          <span class="text-sm text-gray-400">
            {{ displayStartDate }} - {{ displayEndDate }}
          </span>
        </div>

        <!-- Desktop Controls -->
        <div v-if="!isMobile" class="flex items-center gap-4">
          <div class="relative">
            <select
              v-model="filterType"
              @change="handleFilterChange"
              class="appearance-none bg-gray-800 text-white px-4 py-2 pr-10 rounded-lg border border-gray-700"
            >
              <option
                v-for="option in filterOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div
              class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none"
            >
              <svg
                class="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          <DateRangePicker
            v-model="dateRange"
            @change="handleDateRangeChange"
          />

          <button
            @click="generatePDF"
            class="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors duration-200"
          >
            <Download class="w-5 h-5" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      <!-- Mobile Controls -->
      <div v-if="isMobile" class="flex flex-col space-y-3">
        <div class="relative">
          <select
            v-model="filterType"
            @change="handleFilterChange"
            class="w-full appearance-none bg-gray-800 text-white px-4 py-3 pr-10 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option
              v-for="option in filterOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <div
            class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none"
          >
            <svg
              class="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        <button
          @click="showMobileMenu = !showMobileMenu"
          class="flex justify-center items-center gap-3 bg-gray-800 px-4 py-3 rounded-lg text-white border border-gray-700"
        >
          <Calendar class="w-5 h-5" />
          <span>{{
            showMobileMenu ? 'Hide Date Controls' : 'Date Controls'
          }}</span>
        </button>

        <div
          v-if="showMobileMenu"
          class="bg-gray-900 rounded-lg p-4 border border-gray-800 space-y-4"
        >
          <div>
            <label class="block text-sm text-gray-400 mb-3">Date Range</label>
            <div class="grid grid-cols-1 gap-3">
              <input
                type="date"
                v-model="startDate"
                class="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-4 py-3"
              />
              <input
                type="date"
                v-model="endDate"
                class="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-4 py-3"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-400 mb-3">Quick Ranges</label>
            <div class="grid grid-cols-2 gap-2">
              <button
                @click="setPredefinedRange('today')"
                class="px-3 py-2 text-sm bg-gray-800 text-white rounded-lg"
              >
                Today
              </button>
              <button
                @click="setPredefinedRange('yesterday')"
                class="px-3 py-2 text-sm bg-gray-800 text-white rounded-lg"
              >
                Yesterday
              </button>
              <button
                @click="setPredefinedRange('week')"
                class="px-3 py-2 text-sm bg-gray-800 text-white rounded-lg"
              >
                Last 7 Days
              </button>
              <button
                @click="setPredefinedRange('month')"
                class="px-3 py-2 text-sm bg-gray-800 text-white rounded-lg"
              >
                Last 30 Days
              </button>
            </div>
          </div>

          <button
            @click="handleDateRangeChange"
            class="w-full bg-blue-600 text-white px-4 py-3 rounded-lg"
          >
            Apply Changes
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      <div class="bg-gray-900 rounded-lg p-4">
        <p class="text-sm text-gray-400">Total Responses</p>
        <p class="text-2xl font-bold text-white">
          {{ npsData.overall.total_responses }}
        </p>
      </div>
      <div class="bg-gray-900 rounded-lg p-4">
        <p class="text-sm text-gray-400">Overall NPS</p>
        <div class="flex flex-col">
          <p class="text-2xl font-bold text-white mb-1">
            {{ npsData.overall.nps_score }}
          </p>
          <p class="text-xs text-gray-400">
            CI: {{ overallConfidenceInterval?.lower }} to
            {{ overallConfidenceInterval?.upper }}
          </p>
        </div>
      </div>
      <div class="bg-gray-900 rounded-lg p-4 sm:col-span-2 lg:col-span-1">
        <p class="text-sm text-gray-400">Target Industry NPS</p>
        <p class="text-2xl font-bold text-white">
          {{ npsData.target_industry_true?.nps_score || 'N/A' }}
        </p>
      </div>
    </div>

    <!-- Charts Container -->
    <div
      :class="[
        'grid gap-6',
        showTwoColumns ? 'grid-cols-2' : 'grid-cols-1',
        isTabletLandscape ? 'grid-cols-1' : ''
      ]"
    >
      <!-- Distribution Chart -->
      <div v-if="pieChartData" class="bg-gray-900 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-white mb-4">
          Response Distribution
        </h3>
        <div class="w-full h-[250px] sm:h-[300px] lg:h-[350px]">
          <Pie :data="pieChartData" :options="pieChartOptions" />
        </div>
      </div>

      <!-- Daily Trends Chart -->
      <div v-if="dailyChartData" class="bg-gray-900 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-white mb-4">Daily NPS Trends</h3>
        <div class="w-full h-[300px] sm:h-[350px] lg:h-[400px]">
          <Line :data="dailyChartData" :options="lineChartOptions" />
        </div>
      </div>

      <!-- Cumulative Daily Trends Chart -->
      <div v-if="cumulativeChartData" class="bg-gray-900 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-white mb-4">
          Cumulative Daily NPS Trend
        </h3>
        <div class="w-full h-[300px] sm:h-[350px] lg:h-[400px]">
          <Line :data="cumulativeChartData" :options="cumulativeChartOptions" />
        </div>
      </div>

      <!-- Monthly Charts -->
      <div v-if="monthlyChartData" class="bg-gray-900 rounded-lg p-4">
        <div
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4"
        >
          <h3 class="text-lg font-semibold text-white">
            Monthly NPS Score Trend
          </h3>
          <div class="flex flex-wrap gap-2 mt-2 sm:mt-0">
            <button
              v-for="type in ['normal', 'weighted', 'rolling']"
              :key="type"
              @click="npsViewType = type"
              :class="[
                'px-3 py-2 text-sm rounded transition-colors',
                npsViewType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300'
              ]"
            >
              {{ type.charAt(0).toUpperCase() + type.slice(1) }}
            </button>
          </div>
        </div>
        <div class="w-full h-[300px] sm:h-[350px] lg:h-[400px]">
          <Line :data="monthlyChartData" :options="lineChartOptions" />
        </div>
      </div>

      <!-- Cumulative Monthly Trends Chart -->
      <div v-if="cumulativeMonthlyChartData" class="bg-gray-900 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-white mb-4">
          Cumulative Monthly NPS Trend
        </h3>
        <div class="w-full h-[300px] sm:h-[350px] lg:h-[400px]">
          <Line
            :data="cumulativeMonthlyChartData"
            :options="cumulativeChartOptions"
          />
        </div>
      </div>

      <!-- Monthly Distribution Chart -->
      <div
        v-if="monthlyDistributionChartData"
        class="bg-gray-900 rounded-lg p-4"
      >
        <h3 class="text-lg font-semibold text-white mb-4">
          Monthly Response Distribution
        </h3>
        <div class="w-full h-[300px] sm:h-[350px] lg:h-[400px]">
          <Bar
            :data="monthlyDistributionChartData"
            :options="distributionChartOptions"
          />
        </div>
      </div>

      <!-- Language Chart -->
      <div v-if="languageChartData" class="bg-gray-900 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-white mb-4">NPS by Language</h3>
        <div class="w-full h-[300px] sm:h-[350px] lg:h-[400px]">
          <BarChart
            :chart-data="languageChartData"
            :options="{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  position: 'top',
                  labels: {
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 10 : 12 }
                  }
                },
                tooltip: {
                  enabled: true,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  titleColor: '#fff',
                  bodyColor: '#fff',
                  padding: isTabletLandscape.value ? 8 : 12,
                  mode: 'index',
                  intersect: false,
                  callbacks: {
                    title: (tooltipItems) => tooltipItems[0].label,
                    label: (tooltipItem) => {
                      const label = tooltipItem.dataset.label
                      const value = tooltipItem.raw
                      return `${label}: ${value}`
                    },
                    labelColor: (tooltipItem) => {
                      return {
                        backgroundColor: tooltipItem.dataset.backgroundColor,
                        borderColor: tooltipItem.dataset.backgroundColor
                      }
                    },
                    labelTextColor: () => '#ffffff'
                  }
                },
                datalabels: {
                  color: '#fff',
                  font: {
                    weight: 'bold',
                    size: isTabletLandscape.value ? 10 : 12
                  },
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  formatter: (value) => value
                }
              },
              scales: {
                y: {
                  min: -100,
                  max: 100,
                  ticks: {
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 9 : 11 }
                  },
                  grid: {
                    color: '#374151'
                  },
                  title: {
                    display: true,
                    text: 'NPS Score',
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 10 : 12 }
                  }
                },
                y1: {
                  type: 'linear',
                  display: true,
                  position: 'right',
                  min: 0,
                  ticks: {
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 9 : 11 }
                  },
                  grid: {
                    drawOnChartArea: false
                  },
                  title: {
                    display: true,
                    text: 'Total Responses',
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 10 : 12 }
                  }
                },
                x: {
                  ticks: {
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 9 : 11 }
                  },
                  grid: {
                    color: '#374151'
                  }
                }
              }
            }"
          />
        </div>
      </div>

      <!-- Industry Chart -->
      <div v-if="industryChartData" class="bg-gray-900 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-white mb-4">NPS by Industry</h3>
        <div class="w-full h-[300px] sm:h-[350px] lg:h-[400px]">
          <BarChart
            :chart-data="industryChartData"
            :options="{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  position: 'top',
                  labels: {
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 10 : 12 }
                  }
                },
                tooltip: {
                  enabled: true,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  titleColor: '#fff',
                  bodyColor: '#fff',
                  padding: isTabletLandscape.value ? 8 : 12,
                  mode: 'index',
                  intersect: false,
                  callbacks: {
                    title: (tooltipItems) => tooltipItems[0].label,
                    label: (tooltipItem) => {
                      const label = tooltipItem.dataset.label
                      const value = tooltipItem.raw
                      return `${label}: ${value}`
                    },
                    labelColor: (tooltipItem) => {
                      return {
                        backgroundColor: tooltipItem.dataset.backgroundColor,
                        borderColor: tooltipItem.dataset.backgroundColor
                      }
                    },
                    labelTextColor: () => '#ffffff'
                  }
                },
                datalabels: {
                  color: '#fff',
                  font: {
                    weight: 'bold',
                    size: isTabletLandscape.value ? 10 : 12
                  },
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  formatter: (value) => value
                }
              },
              scales: {
                y: {
                  ticks: {
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 9 : 11 }
                  },
                  grid: {
                    color: '#374151'
                  }
                },
                x: {
                  ticks: {
                    color: '#fff',
                    font: { size: isTabletLandscape.value ? 9 : 11 }
                  },
                  grid: {
                    color: '#374151'
                  }
                }
              }
            }"
          />
        </div>
      </div>

      <!-- Themes Analysis -->
      <div
        v-if="themesData"
        class="bg-gray-900 rounded-lg p-4"
        :class="showTwoColumns && !isTabletLandscape ? 'col-span-2' : ''"
      >
        <h3 class="text-lg font-semibold text-white mb-6">Thematic Analysis</h3>

        <div class="space-y-4 mb-6">
          <div>
            <label class="block text-sm text-gray-400 mb-3">Reason Theme</label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="theme in uniqueSuggestionThemes"
                :key="theme"
                @click="selectedSuggestionTheme = theme"
                :class="
                  selectedSuggestionTheme === theme
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300'
                "
                class="px-3 py-2 text-sm rounded transition-colors"
              >
                {{
                  theme.charAt(0).toUpperCase() + theme.slice(1).toLowerCase()
                }}
              </button>
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-400 mb-3"
              >Improvement Theme</label
            >
            <div class="flex flex-wrap gap-2">
              <button
                v-for="theme in uniqueImprovementThemes"
                :key="theme"
                @click="selectedImprovementTheme = theme"
                :class="
                  selectedImprovementTheme === theme
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300'
                "
                class="px-3 py-2 text-sm rounded transition-colors"
              >
                {{
                  theme.charAt(0).toUpperCase() + theme.slice(1).toLowerCase()
                }}
              </button>
            </div>
          </div>
        </div>

        <div
          class="flex justify-between items-center mb-6 p-3 bg-gray-800 rounded-lg"
        >
          <div class="text-sm text-gray-400">
            Showing {{ filteredThemesData?.length || 0 }} results
          </div>
          <button
            v-if="
              selectedSuggestionTheme !== 'all' ||
              selectedImprovementTheme !== 'all'
            "
            @click="
              () => {
                selectedSuggestionTheme = 'all'
                selectedImprovementTheme = 'all'
              }
            "
            class="text-sm text-blue-400 hover:text-blue-300"
          >
            Clear all filters
          </button>
        </div>

        <!-- Mobile Table View -->
        <div v-if="isMobile" class="space-y-4">
          <div
            v-for="(theme, index) in filteredThemesData?.slice(0, 50)"
            :key="index"
            class="bg-gray-800 rounded-lg p-4 space-y-3"
          >
            <div class="flex justify-between items-start">
              <div class="text-sm text-gray-400">
                {{ new Date(theme.created_at).toLocaleDateString() }}
              </div>
              <div class="text-lg font-bold text-white">{{ theme.score }}</div>
            </div>
            <div class="space-y-2">
              <div>
                <div class="text-xs text-gray-500 uppercase">Reason</div>
                <div class="text-sm text-white">{{ theme.reason }}</div>
              </div>
              <div>
                <div class="text-xs text-gray-500 uppercase">Suggestion</div>
                <div class="text-sm text-white">{{ theme.suggestion }}</div>
              </div>
              <div class="flex flex-wrap gap-2 pt-2">
                <span
                  class="px-2 py-1 text-xs bg-blue-900 text-blue-200 rounded"
                >
                  {{ theme.suggestion_theme }}
                </span>
                <span
                  class="px-2 py-1 text-xs bg-purple-900 text-purple-200 rounded"
                >
                  {{
                    theme.improvement_theme === 'NULL_IMPROV'
                      ? 'None'
                      : theme.improvement_theme
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Table View -->
        <div v-else class="overflow-x-auto">
          <div class="min-w-[800px]">
            <table class="w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                  >
                    Date
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                  >
                    Score
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                  >
                    Reason
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                  >
                    Suggestion
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                  >
                    Reason Theme
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                  >
                    Improvement Theme
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-700">
                <tr
                  v-for="(theme, index) in filteredThemesData?.slice(0, 100)"
                  :key="index"
                  class="hover:bg-gray-800"
                >
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {{ new Date(theme.created_at).toLocaleDateString() }}
                  </td>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white"
                  >
                    {{ theme.score }}
                  </td>
                  <td class="px-6 py-4 text-sm text-white max-w-xs">
                    {{ theme.reason }}
                  </td>
                  <td class="px-6 py-4 text-sm text-white max-w-xs">
                    {{ theme.suggestion }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">
                    <span
                      class="px-2 py-1 text-xs bg-blue-900 text-blue-200 rounded"
                    >
                      {{ theme.suggestion_theme }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">
                    <span
                      class="px-2 py-1 text-xs bg-purple-900 text-purple-200 rounded"
                    >
                      {{
                        theme.improvement_theme === 'NULL_IMPROV'
                          ? 'None'
                          : theme.improvement_theme
                      }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
input[type='date']::-webkit-calendar-picker-indicator {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
}
</style>